import * as React from 'react';
import { ViewProps } from 'react-native';

import createElement from 'react-native-web/dist/exports/createElement';
import css from 'react-native-web/dist/exports/StyleSheet/css';
import * as forwardedProps from 'react-native-web/dist/modules/forwardedProps';
import useElementLayout from 'react-native-web/dist/modules/useElementLayout';
import useMergeRefs from 'react-native-web/dist/modules/useMergeRefs';
import usePlatformMethods from 'react-native-web/dist/modules/usePlatformMethods';
import useResponderEvents from 'react-native-web/dist/modules/useResponderEvents';
import StyleSheet from 'react-native-web/dist/exports/StyleSheet';
import TextAncestorContext from 'react-native-web/dist/exports/Text/TextAncestorContext';

import { createPickProps } from './helpers/createPickProps';
import { ElementFromTagName } from './helpers/ElementFromTagName';

const forwardPropsList = {
  ...forwardedProps.defaultProps,
  ...forwardedProps.accessibilityProps,
  ...forwardedProps.clickProps,
  ...forwardedProps.focusProps,
  ...forwardedProps.keyboardProps,
  ...forwardedProps.mouseProps,
  ...forwardedProps.touchProps,
  ...forwardedProps.styleProps,
  href: true,
  download: true,
  lang: true,
  onScroll: true,
  onWheel: true,
  pointerEvents: true,
};

type AllowedTagName = 'div' | 'form' | 'label' | 'a';

type AllowedProps<T extends AllowedTagName> = Omit<
  React.ComponentProps<T>,
  'ref' | 'key' | 'children' | 'id' | keyof ViewProps
>;

// Fixed: Re-implemented to support additional props
const pickProps = createPickProps(forwardPropsList);

export default function createViewComponent<
  T extends AllowedTagName,
  PropNames extends keyof AllowedProps<T>
>(params: { tagName: T; additionalProps: { [K in PropNames]: true } }) {
  type Props = {
    [K in PropNames]?: AllowedProps<T>[K];
  } & { children?: React.ReactNode };
  const View = React.forwardRef<ElementFromTagName<T>, Props & ViewProps>(
    (props, forwardedRef) => {
      // NOTE: Everything from here until the end of this function, with the
      // exception of blocks marked as "Fixed", is sourced directly from:
      // https://github.com/necolas/react-native-web/blob/0.17.5/packages/react-native-web/src/exports/View/index.js
      const {
        hrefAttrs,
        onLayout,
        onMoveShouldSetResponder,
        onMoveShouldSetResponderCapture,
        onResponderEnd,
        onResponderGrant,
        onResponderMove,
        onResponderReject,
        onResponderRelease,
        onResponderStart,
        onResponderTerminate,
        onResponderTerminationRequest,
        onScrollShouldSetResponder,
        onScrollShouldSetResponderCapture,
        onSelectionChangeShouldSetResponder,
        onSelectionChangeShouldSetResponderCapture,
        onStartShouldSetResponder,
        onStartShouldSetResponderCapture,
      } = props;

      if (process.env.NODE_ENV !== 'production') {
        React.Children.toArray(props.children).forEach((item) => {
          if (typeof item === 'string') {
            // eslint-disable-next-line no-console
            console.error(
              `Unexpected text node: ${item}. A text node cannot be a child of a <View>.`,
            );
          }
        });
      }

      const hasTextAncestor = React.useContext(TextAncestorContext);
      const hostRef = React.useRef(null);

      useElementLayout(hostRef, onLayout);
      useResponderEvents(hostRef, {
        onMoveShouldSetResponder,
        onMoveShouldSetResponderCapture,
        onResponderEnd,
        onResponderGrant,
        onResponderMove,
        onResponderReject,
        onResponderRelease,
        onResponderStart,
        onResponderTerminate,
        onResponderTerminationRequest,
        onScrollShouldSetResponder,
        onScrollShouldSetResponderCapture,
        onSelectionChangeShouldSetResponder,
        onSelectionChangeShouldSetResponderCapture,
        onStartShouldSetResponder,
        onStartShouldSetResponderCapture,
      });

      // Fixed: Changed from 'div' to params.tagName
      let component = params.tagName as string;
      const style = StyleSheet.compose(
        hasTextAncestor && styles.inline,
        props.style,
      );

      // Fixed: Added second argument
      const supportedProps = pickProps(props, params.additionalProps);

      // Fixed: Moved into this function from bottom of file
      const classList = [classes.view];
      // Fixed: Added this `if` block to support className
      if (supportedProps.className) {
        classList.push(supportedProps.className as string);
        delete supportedProps.className;
      }

      supportedProps.classList = classList;
      supportedProps.style = style;
      if (props.href != null) {
        component = 'a';
        if (hrefAttrs != null) {
          const { download, rel, target } = hrefAttrs;
          if (download != null) {
            supportedProps.download = download;
          }
          if (rel != null) {
            supportedProps.rel = rel;
          }
          if (typeof target === 'string') {
            supportedProps.target =
              target.charAt(0) !== '_' ? '_' + target : target;
          }
        }
      }

      const platformMethodsRef = usePlatformMethods(supportedProps);
      const setRef = useMergeRefs(hostRef, platformMethodsRef, forwardedRef);

      supportedProps.ref = setRef;

      return createElement(component, supportedProps);
    },
  );

  View.displayName = 'View';

  return View;
}

const classes = css.create({
  view: {
    alignItems: 'stretch',
    border: '0 solid black',
    boxSizing: 'border-box',
    display: 'flex',
    flexBasis: 'auto',
    flexDirection: 'column',
    flexShrink: 0,
    margin: 0,
    minHeight: 0,
    minWidth: 0,
    padding: 0,
    position: 'relative',
    zIndex: 0,
  },
});

const styles = StyleSheet.create({
  inline: {
    display: 'inline-flex',
  },
});
